import React, { useEffect, useRef } from 'react'
import {
  makePopupFeatures,
  postInitialDataToPopup,
  usePopupMessageReceiver,
} from '@components/ConnectedPopupWindow/utils'

interface AuthorizationPopupProps {
  urlToOpen: string
  widthPx?: number
  heightPx?: number
  onPopupOpen: (popup: Window) => void
  onPopupClose: (newData?: unknown) => void
  popupName?: string
  initialData?: unknown
}

export const ConnectedPopupWindow: React.FC<AuthorizationPopupProps> = ({
  urlToOpen,
  widthPx = 700,
  heightPx = 550,
  onPopupOpen,
  onPopupClose,
  initialData,
  popupName = 'authPopup',
}) => {
  const popup = useRef<Window | null>(null)
  const isClosedRef = useRef<boolean>(false)
  const intervalId = useRef<NodeJS.Timer | null>()

  const { dataFromPopup } = usePopupMessageReceiver(popup)

  function isNullOrUndefined<T>(
    value: T | undefined | null
  ): value is null | undefined {
    return value === undefined || value === null
  }

  // Handle opening on new popup and posting of initialData
  useEffect(() => {
    // opening a new window as popup
    const features = makePopupFeatures(widthPx, heightPx)
    popup.current = window.open(urlToOpen, popupName, features)
    const currentPopup = popup.current

    if (!currentPopup) {
      onPopupClose()
      return
    }

    //  we send the instance of the just opened popup as callback
    onPopupOpen(currentPopup)
    currentPopup.focus()

    // send initial data to popup
    postInitialDataToPopup({
      initialData,
      popup: currentPopup,
    })

    // cleanup functions
    return () => {
      // In case our component in un-mounted, we want to automatically close our popup
      if (popup.current) {
        popup.current.close()
        onPopupClose()
      }
    }
  }, [])

  // constantly check if popup is closed, to sync outer state
  useEffect(() => {
    const currentPopup = popup.current
    if (currentPopup) {
      intervalId.current = setInterval(() => {
        if (currentPopup.closed && !isClosedRef.current) {
          isClosedRef.current = true
          onPopupClose()
        }
      }, 500)
    }

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current)
      }
    }
  }, [popup])

  // controlling the receival of data from popup
  useEffect(() => {
    if (dataFromPopup) {
      // console.log('received new data from popup: ', dataFromPopup)
      if (
        !isNullOrUndefined(popupName) &&
        !isNullOrUndefined(dataFromPopup.role) &&
        dataFromPopup.role !== popupName
      ) {
        return
      }
      onPopupClose(dataFromPopup)
      popup.current!.close()
    }
  }, [dataFromPopup])

  return null
}
